/* eslint-disable react/forbid-prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';

import {
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    FormHelperText,
} from '@material-ui/core';

import AppConfigRobotType from '~/components/AppConfigRobotType';
import FormCheckList from '~/components/Form/CheckList';
import LocaleMessage from '~/components/LocaleMessage';

import api from '~/services/pluginbot-api';

const center = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
};

const warnings = {
    cruzr: [],
    nao: ['page.applications.conversation.warning.languages.single'],
    temi: ['page.applications.conversation.warning.languages.single'],
};

const min_version = {
    cruzr: 'warnings.version.cruzr_v3120',
    temi: 'warnings.version.temi_v270',
};

const GENERATOR_VOICE_TAG = 'generator';

export default function LanguageConfig({
    appSettings,
    handleError,
    robotTypes,
    updateObject,
    currType,
    setCurrType,
    addons,
}) {
    const { settings } = appSettings;
    const { plugins } = addons;
    const allow_generators = !!(
        plugins &&
        plugins.find(p => {
            return p.slug === 'generators';
        })
    );

    const langSettings = settings && settings.language ? settings.language : {};
    const r_type = currType ? currType.slug : '';

    const [language, setLanguage] = useState(langSettings);
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [voices, setVoices] = useState({});
    const [pluginVoices, setPluginVoices] = useState([]);
    const [, setNeedSaving] = useState(false);

    async function handleSubmit() {
        const data = { language };
        updateObject(data);
        setNeedSaving(false);
    }

    function handleCurrTypeEdit(type, key, value) {
        const type_settings = language[type] || {};
        type_settings[key] = value;
        setLanguage({ ...language, [type]: type_settings });
        setNeedSaving(true);
    }

    async function loadLanguages() {
        await api
            .get(`applications/conversation/languages`)
            .then(response => {
                const { data } = response;
                const langs = data.languages || [];
                setAvailableLanguages(langs);
            })
            .catch(e => {
                handleError(e);
            });
    }

    async function loadVoices() {
        const r_types = robotTypes.map(r => r.slug);

        await api
            .get(
                `applications/conversation/voices?types=${JSON.stringify(
                    r_types
                )}`
            )
            .then(response => {
                const { data } = response;
                // const r_voices = [];
                // Object.keys(all_voices).forEach(t => {
                //     const t_voices = all_voices[t];
                //     t_voices.forEach(voice => {
                //         r_voices.push({
                //             type: t,
                //             key: `${t}_${voice.value}`,
                //             ...voice,
                //         });
                //     });
                // });
                // setVoices(r_voices);
                setVoices(data);
            })
            .catch(e => {
                handleError(e);
            });
    }

    async function loadGeneratorVoices() {
        await api
            .get(`generators/voices`)
            .then(response => {
                const { data } = response;
                if (data && typeof data === 'object' && data[0]) {
                    setPluginVoices(data);
                }
            })
            .catch(e => {
                handleError(e);
            });
    }

    useEffect(() => {
        loadVoices();
        loadGeneratorVoices();
        loadLanguages();
    }, []);

    function handleVoiceSourceEdit(type, source) {
        handleCurrTypeEdit(type, 'source', source);

        if (source === GENERATOR_VOICE_TAG) {
            handleCurrTypeEdit(type, 'gender', '');
        }

        const curr_gender =
            language && language[type] && language[type].gender
                ? language[type].gender
                : '';

        const r_voices = voices[r_type] || {};
        const source_voices = r_voices[source] || [];
        const available_genders = {};
        source_voices.forEach(v => {
            const v_genders = v.genders;
            v_genders.forEach(g => {
                available_genders[g] = true;
            });
        });
        const gender_options = Object.keys(available_genders).map(g => {
            return g;
        });

        if (!available_genders[curr_gender]) {
            handleCurrTypeEdit(type, 'gender', gender_options[0] || '');
        }
    }

    function renderLanguages() {
        const options = availableLanguages.map(l => {
            return {
                id: l.value,
                name: <LocaleMessage msg={`list.languages.${l.value}`} />,
            };
        });

        return (
            <div>
                <LocaleMessage msg="page.applications.conversation.language.list" />
                <br />
                <FormCheckList
                    settings={language}
                    options={options}
                    allowStar
                    multiple
                    starKey="main"
                    onChange={l => {
                        setLanguage({
                            ...language,
                            ...l,
                        });
                        setNeedSaving(true);
                    }}
                />
            </div>
        );
    }

    function renderGeneratorPluginSettings(v_settings) {
        const curr_voice = v_settings.voice ? v_settings.voice : '';
        return (
            <FormControl fullWidth>
                <InputLabel>
                    <LocaleMessage msg="page.applications.conversation.language.voice" />
                </InputLabel>
                <Select
                    value={curr_voice}
                    onChange={event =>
                        handleCurrTypeEdit(r_type, 'voice', event.target.value)
                    }
                >
                    {pluginVoices.map(v => (
                        <MenuItem value={v.id} key={`lang_${v.id}`}>
                            {v.name}
                        </MenuItem>
                    ))}
                </Select>
                <FormHelperText>
                    <LocaleMessage msg="page.applications.conversation.warning.voice" />
                </FormHelperText>
            </FormControl>
        );
    }

    function renderDefaultVoiceSettings(value, options) {
        return (
            <FormControl fullWidth>
                <InputLabel>
                    <LocaleMessage msg="page.applications.conversation.language.gender" />
                </InputLabel>
                <Select
                    value={value}
                    onChange={event =>
                        handleCurrTypeEdit(r_type, 'gender', event.target.value)
                    }
                >
                    {options.map(g => (
                        <MenuItem value={g} key={`lang_${g}`}>
                            <LocaleMessage msg={`list.genders.${g}`} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }

    function renderVoiceSelection() {
        const r_warnings = warnings[r_type] || [];

        const r_voices = voices[r_type] || {};
        const v_sources = r_voices.sources || ['internal'];

        const r_lang_settings =
            language && language[r_type] ? language[r_type] : {};

        const curr_source = r_lang_settings.source
            ? r_lang_settings.source
            : 'internal';

        const source_voices = r_voices[curr_source] || [];
        const available_genders = {};
        source_voices.forEach(v => {
            const v_genders = v.genders;
            v_genders.forEach(g => {
                available_genders[g] = true;
            });
        });
        const gender_options = Object.keys(available_genders).map(g => {
            return g;
        });
        const curr_gender = r_lang_settings.gender
            ? r_lang_settings.gender
            : '';

        return (
            <div
                className="col-12 row"
                style={{ display: 'flex', justifyContent: 'space-between' }}
            >
                <div className="col-md-5 col-12 mb-3">{renderLanguages()}</div>
                <AppConfigRobotType
                    className="col-md-6 col-12 mb-5"
                    robotTypes={robotTypes}
                    currType={currType}
                    setCurrType={t => setCurrType(t)}
                >
                    <div style={center}>
                        {r_warnings.map(w => {
                            return (
                                <p key={w} style={{ textAlign: 'center' }}>
                                    <LocaleMessage msg={w} />
                                </p>
                            );
                        })}
                        <div className="col-md-8 col-12 mt-3 mb-5">
                            <FormControl fullWidth>
                                <InputLabel>
                                    <LocaleMessage msg="page.applications.conversation.language.source" />
                                    *
                                </InputLabel>
                                <Select
                                    value={curr_source}
                                    onChange={event =>
                                        handleVoiceSourceEdit(
                                            r_type,
                                            event.target.value
                                        )
                                    }
                                >
                                    {v_sources.map(s => (
                                        <MenuItem
                                            value={s}
                                            key={`vsrouce_${s}`}
                                        >
                                            <LocaleMessage
                                                msg={`page.applications.conversation.language.source.${s}`}
                                            />
                                        </MenuItem>
                                    ))}
                                    {allow_generators ? (
                                        <MenuItem value={GENERATOR_VOICE_TAG}>
                                            <LocaleMessage msg="page.applications.conversation.language.source.generators" />
                                        </MenuItem>
                                    ) : null}
                                </Select>
                                {min_version[r_type] ? (
                                    <FormHelperText>
                                        <LocaleMessage
                                            msg={min_version[r_type]}
                                        />
                                    </FormHelperText>
                                ) : null}
                            </FormControl>
                        </div>

                        <div className="col-md-8 col-12 mb-5">
                            {allow_generators &&
                            curr_source === GENERATOR_VOICE_TAG
                                ? renderGeneratorPluginSettings(r_lang_settings)
                                : renderDefaultVoiceSettings(
                                      curr_gender,
                                      gender_options
                                  )}
                        </div>
                    </div>
                </AppConfigRobotType>
            </div>
        );
    }

    return (
        <div className="col-12 mb-3 row">
            <div className="sidecard-header">
                <h2>
                    <LocaleMessage msg="page.applications.conversation.language.title" />
                </h2>
            </div>
            <div
                className="col-12 mb-3"
                style={{
                    padding: '0px 20px',
                }}
            >
                <br />
                <LocaleMessage msg="page.applications.conversation.warning.languages" />
            </div>
            <div className="sidecard-body">
                <div className="row" style={center}>
                    {renderVoiceSelection()}
                    <div className="col-md-6 col-12 mb-5">
                        <Button
                            className="p-3"
                            variant="contained"
                            color="primary"
                            onClick={event => handleSubmit(event)}
                            fullWidth
                            size="large"
                            // disabled={!needSaving}
                        >
                            <LocaleMessage msg="button.save" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

LanguageConfig.defaultProps = {
    addons: {},
};

LanguageConfig.propTypes = {
    updateObject: PropTypes.func.isRequired,
    handleError: PropTypes.func.isRequired,
    appSettings: PropTypes.object.isRequired,
    robotTypes: PropTypes.array.isRequired,
    currType: PropTypes.object.isRequired,
    setCurrType: PropTypes.func.isRequired,
    addons: PropTypes.object,
};
